import { describeCondition, type CreateRuleDefinitionDto, type RuleDefinitionDto, type RuleTargetDto, type UpdateRuleDefinitionDto } from '@citilight-monitor/contracts';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useCreateRule, useDeleteRule, useUpdateRule } from '../../lib/api';
import { usePagination } from '../../lib/pagination';
import { useToast } from '../../lib/toast';
import { Button, EmptyState, StatusChip, Table } from '../atoms';
import { Pagination } from '../molecules';
import { RuleFormDialog } from './RuleFormDialog';

const PAGE_SIZE = 10;

export function RulesTable({ rules, targets }: { rules: RuleDefinitionDto[]; targets: RuleTargetDto[] }) {
  const [dialogRule, setDialogRule] = useState<RuleDefinitionDto | undefined>(undefined);
  const [dialogOpen, setDialogOpen] = useState(false);
  const { showSuccess } = useToast();
  const createRule = useCreateRule();
  const updateRule = useUpdateRule();
  const deleteRule = useDeleteRule();
  const { page, totalPages, pageItems, start, end, total, setPage } = usePagination(rules, PAGE_SIZE);

  function openCreate() {
    setDialogRule(undefined);
    setDialogOpen(true);
  }

  function openEdit(rule: RuleDefinitionDto) {
    setDialogRule(rule);
    setDialogOpen(true);
  }

  async function remove(rule: RuleDefinitionDto) {
    if (!window.confirm(`Delete the rule on "${rule.ruleTargetName}"?`)) return;
    await deleteRule.mutateAsync(rule.id);
    showSuccess('Rule deleted.');
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end px-5 pt-4">
        <Button variant="primary" icon={<Plus size={14} aria-hidden />} onClick={openCreate}>
          Add rule
        </Button>
      </div>

      <Table>
        <thead>
          <tr>
            <th scope="col">Target</th>
            <th scope="col">Condition</th>
            <th scope="col">Severity</th>
            <th scope="col">State</th>
            <th scope="col" aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {pageItems.map((rule) => (
            <tr key={rule.id}>
              <td>
                <div className="font-medium text-fg">{rule.name || rule.ruleTargetName}</div>
                {rule.name && <div className="text-xs text-fg-muted">{rule.ruleTargetName}</div>}
              </td>
              <td className="font-mono text-xs">{describeCondition(rule.metricLabel, 'number', rule.operator, rule.threshold)}</td>
              <td>
                <StatusChip tone={rule.severity === 'critical' ? 'critical' : 'warning'} label={rule.severity} />
              </td>
              <td>
                <StatusChip tone={rule.enabled ? 'good' : 'neutral'} label={rule.enabled ? 'Enabled' : 'Disabled'} />
              </td>
              <td>
                <div className="flex justify-end gap-1">
                  <Button variant="ghost" size="icon" aria-label="Edit" onClick={() => openEdit(rule)} icon={<Pencil size={14} aria-hidden />} />
                  <Button variant="ghost" size="icon" aria-label="Delete" onClick={() => void remove(rule)} icon={<Trash2 size={14} aria-hidden />} />
                </div>
              </td>
            </tr>
          ))}
          {rules.length === 0 && (
            <tr>
              <td colSpan={5}>
                <EmptyState>No rules yet. Add one to start alerting on a metric.</EmptyState>
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      <Pagination page={page} totalPages={totalPages} start={start} end={end} total={total} onPageChange={setPage} />

      <RuleFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        targets={targets}
        rule={dialogRule}
        onSubmit={async (dto) => {
          if (dialogRule) {
            await updateRule.mutateAsync({ id: dialogRule.id, body: dto as UpdateRuleDefinitionDto });
            showSuccess('Rule updated.');
          } else {
            await createRule.mutateAsync(dto as CreateRuleDefinitionDto);
            showSuccess('Rule added.');
          }
        }}
      />
    </div>
  );
}
